import { createFileRoute, Link, Outlet, useChildMatches } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Megaphone, Plus, Pause, Play, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePaymentStatus } from "@/hooks/use-payment-status";
import { PaymentOverdueBanner } from "@/components/payment-overdue-banner";
import { triggerCampaignAction, type CampaignAction } from "@/lib/n8n-webhook";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/campaigns")({
  component: CampaignsLayout,
});

type CampaignRow = {
  id: string;
  ads_name: string;
  objective: string;
  status: string;
  created_at: string;
  ad_id: string | null;
  end_date: string | null;
};

function statusStyles(status: string) {
  const s = status.toLowerCase();
  if (s === "active" || s === "published") return "bg-success/15 text-success";
  if (s === "failed" || s === "error" || s === "rejected") return "bg-destructive/15 text-destructive";
  if (s === "paused") return "bg-muted text-muted-foreground";
  // processing / pending / in review / default
  return "bg-warning/15 text-warning";
}

function CampaignsLayout() {
  // Use child route matches (not pathname) so we don't flicker to <Outlet/>
  // during a route transition AWAY from /campaigns, which has no child match
  // and would cause a Suspense/render error (#300) before unmount.
  const childMatches = useChildMatches();
  const hasChildRoute = childMatches.length > 0;

  const [rows, setRows] = useState<CampaignRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const { isBlocked } = usePaymentStatus();

  useEffect(() => {
    if (hasChildRoute) return;
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        if (!cancelled) setRows([]);
        return;
      }
      const { data, error } = await supabase
        .from("campaigns")
        .select("id, ads_name, objective, status, created_at, ad_id, end_date" as any)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (error) {
        setError(error.message);
        setRows([]);
      } else {
        setRows((data ?? []) as unknown as CampaignRow[]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasChildRoute]);

  if (hasChildRoute) return <Outlet />;

  async function handleAction(row: CampaignRow) {
    const isActive = row.status.toLowerCase() === "active";
    const action: CampaignAction = isActive ? "pause" : "resume";
    const nextStatus = isActive ? "Paused" : "Active";
    const prevStatus = row.status;

    if (!row.ad_id) {
      toast.error("Missing ad_id for this campaign.");
      return;
    }

    setPendingId(row.id);
    // Optimistic UI update
    setRows((prev) =>
      prev ? prev.map((r) => (r.id === row.id ? { ...r, status: nextStatus } : r)) : prev,
    );

    try {
      await triggerCampaignAction({ ad_id: row.ad_id, action });
      toast.success(`Campaign ${action}d`);
    } catch (e) {
      // Revert on failure
      setRows((prev) =>
        prev ? prev.map((r) => (r.id === row.id ? { ...r, status: prevStatus } : r)) : prev,
      );
      toast.error(e instanceof Error ? e.message : `Failed to ${action} campaign`);
    } finally {
      setPendingId((id) => (id === row.id ? null : id));
    }
  }

  return (
    <AppShell title="Campaigns">
      <div className="mx-auto max-w-7xl space-y-6">
        {isBlocked && <PaymentOverdueBanner />}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold">Campaigns</h2>
            <p className="text-sm text-muted-foreground">
              Submit and track Meta Ads campaigns managed by NexAEO.
            </p>
          </div>
          <Button asChild={!isBlocked} variant="hero" size="lg" disabled={isBlocked} aria-disabled={isBlocked}>
            {isBlocked ? (
              <span><Plus /> New Campaign</span>
            ) : (
              <Link to="/campaigns/new">
                <Plus /> New Campaign
              </Link>
            )}
          </Button>
        </div>

        <div className="surface-panel overflow-hidden">
          {/* Desktop header */}
          <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <div className="col-span-4">Name</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Action</div>
            <div className="col-span-2">End Date</div>
            <div className="col-span-2 text-right">Submitted</div>
          </div>

          {rows === null && (
            <div className="p-5 space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}

          {rows && rows.length === 0 && (
            <div className="px-5 py-12 text-center">
              <div className="mx-auto h-10 w-10 rounded-md bg-accent/40 grid place-items-center mb-3">
                <Megaphone className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">
                {error ? `Couldn't load campaigns: ${error}` : "No campaigns yet. Launch your first one to get started."}
              </p>
              {!error && !isBlocked && (
                <Button asChild variant="hero" size="sm" className="mt-4">
                  <Link to="/campaigns/new">
                    <Plus /> New Campaign
                  </Link>
                </Button>
              )}
            </div>
          )}

          {rows && rows.map((c) => {
            const s = c.status.toLowerCase();
            const isActive = s === "active";
            const isPaused = s === "paused";
            const showAction = isActive || isPaused;
            const isPending = pendingId === c.id;
            const statusBadge = (
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs whitespace-nowrap",
                  statusStyles(c.status),
                )}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                {c.status}
              </span>
            );
            const endDateObj = c.end_date ? new Date(`${c.end_date}T23:59:59`) : null;
            const isEnded = !!endDateObj && new Date() > endDateObj;
            const resumeDisabledByEnd = isPaused && isEnded;
            const actionDisabled = isPending || !c.ad_id || resumeDisabledByEnd;
            const rawActionButton = showAction ? (
              <Button
                variant="outline"
                size="sm"
                disabled={actionDisabled}
                onClick={() => handleAction(c)}
                className={cn(resumeDisabledByEnd && "opacity-50 cursor-not-allowed")}
              >
                {isPending ? (
                  <Loader2 className="animate-spin" />
                ) : isActive ? (
                  <Pause />
                ) : (
                  <Play />
                )}
                {isActive ? "Pause" : "Resume"}
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            );
            const actionButton = resumeDisabledByEnd ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">{rawActionButton}</span>
                  </TooltipTrigger>
                  <TooltipContent>Campaign Ended</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              rawActionButton
            );
            const submitted = new Date(c.created_at).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "2-digit",
            });
            const endDateLabel = endDateObj
              ? endDateObj.toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "2-digit",
                })
              : "—";

            return (
              <div
                key={c.id}
                className="border-b border-border last:border-0 hover:bg-accent/30 transition"
              >
                {/* Desktop row */}
                <div className="hidden md:grid grid-cols-12 gap-3 items-center px-5 py-4">
                  <div className="col-span-4 flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-md bg-accent/40 grid place-items-center shrink-0">
                      <Megaphone className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.ads_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{c.objective}</div>
                    </div>
                  </div>
                  <div className="col-span-2">{statusBadge}</div>
                  <div className="col-span-2">{actionButton}</div>
                  <div className="col-span-2 text-sm text-muted-foreground whitespace-nowrap">
                    {endDateLabel}
                  </div>
                  <div className="col-span-2 text-right text-sm text-muted-foreground whitespace-nowrap">
                    {submitted}
                  </div>
                </div>

                {/* Mobile card */}
                <div className="md:hidden p-4 space-y-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-md bg-accent/40 grid place-items-center shrink-0">
                      <Megaphone className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{c.ads_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{c.objective}</div>
                    </div>
                    {statusBadge}
                  </div>
                  <div className="flex items-center justify-between gap-3 pl-12">
                    <div className="flex flex-col text-xs text-muted-foreground whitespace-nowrap">
                      <span>Submitted: {submitted}</span>
                      <span>Ends: {endDateLabel}</span>
                    </div>
                    {actionButton}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
